import React, { useState, useEffect } from 'react'
import { 
  LogOut, Users, Calendar, Home, 
  Settings, UserCheck, Clock, AlertTriangle,
  Building, BookOpen, UserPlus, FileBarChart, Mail, Layers, CalendarOff,
  Receipt, Menu, X, ChevronLeft, ShieldCheck, BarChart4, Shield
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useNavigationGuard } from '../context/NavigationGuardContext'

interface LayoutProps {
  children: React.ReactNode
  currentPage?: string
  onNavigate?: (page: string) => void
}

export function Layout({ children, currentPage = 'dashboard', onNavigate }: LayoutProps) {
  const { userProfile, signOut } = useAuth()
  const { requestNavigation } = useNavigationGuard()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleSignOut = () => {
    requestNavigation(() => {
      void signOut()
    })
  }

  const handleNavigate = (page: string) => {
    onNavigate?.(page)
    setSidebarOpen(false)
  }

  // Sortie vers l'autre espace de l'application : on passe par la garde de
  // navigation pour ne pas perdre un formulaire en cours de saisie.
  const handleLeaveStaffSpace = (to: string) => {
    requestNavigation(() => navigate(to))
  }

  const getNavigationItems = () => {
    const baseItems = [
      { id: 'dashboard', name: 'Tableau de bord', icon: Home },
    ]

    const adminItems = [
      { id: 'sessions', name: 'Sessions', icon: Calendar },
      { id: 'registrations', name: 'Inscriptions', icon: UserPlus },
      { id: 'kids', name: 'Enfants', icon: Users },
      { id: 'parents', name: 'Parents', icon: Users },
      { id: 'centers', name: 'Centres', icon: Building },
      { id: 'stages', name: 'Stages', icon: BookOpen },
      { id: 'waiting-list', name: 'Liste d\'attente', icon: Clock },
      { id: 'cancellations', name: 'Annulations', icon: AlertTriangle },
      { id: 'inclusion', name: 'Inclusion', icon: Shield },
      { id: 'communication', name: 'Communication', icon: Mail },
      { id: 'users', name: 'Utilisateurs', icon: ShieldCheck },
      { id: 'groups', name: 'Gestion groupes', icon: Layers },
      { id: 'admin-presences', name: 'Présences', icon: UserCheck },
      { id: 'reports', name: 'Rapports', icon: FileBarChart },
      { id: 'billing', name: 'Facturation', icon: Receipt },
      { id: 'public-holidays', name: 'Jours fériés', icon: CalendarOff },
      { id: 'settings', name: 'Paramètres', icon: Settings },
    ]

    const animItems = [
      { id: 'presences', name: 'Présences', icon: UserCheck },
      { id: 'my-sessions', name: 'Mes sessions', icon: Calendar },
      { id: 'kids-info', name: 'Infos enfants', icon: Users },
    ]

    const coordiItems = [
      { id: 'center-sessions', name: 'Sessions du centre', icon: Calendar },
      { id: 'center-presences', name: 'Présences', icon: UserCheck },
      { id: 'center-groups', name: 'Gestion groupes', icon: Layers },
      { id: 'center-kids', name: 'Enfants du centre', icon: Users },
      { id: 'inclusion', name: 'Inclusion', icon: Shield },
    ]

    if (userProfile?.role === 'admin') {
      return [...baseItems, ...adminItems]
    } else if (userProfile?.role === 'anim') {
      return [...baseItems, ...animItems]
    } else if (userProfile?.role === 'coordi') {
      return [...baseItems, ...coordiItems]
    }

    return baseItems
  }

  const navigation = getNavigationItems()

  const sidebarContent = (
    <ul className="space-y-1">
      {navigation.map((item) => {
        const Icon = item.icon
        return (
          <li key={item.id}>
            <button
              onClick={() => handleNavigate(item.id)}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                currentPage === item.id || (item.id === 'kids' && currentPage?.startsWith('kids-'))
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              title={sidebarCollapsed ? item.name : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && <span className="font-medium">{item.name}</span>}
            </button>
          </li>
        )
      })}
    </ul>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                Gestion CDV Eclosion
              </h1>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              {userProfile?.role === 'admin' && (
                <button
                  onClick={() => handleLeaveStaffSpace('/admin')}
                  className="flex items-center space-x-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  title="Aller vers le tableau de bord de l’administration"
                >
                  <BarChart4 className="h-4 w-4" />
                  <span className="text-sm hidden md:inline">Tableau de bord</span>
                </button>
              )}
              <div className="text-sm text-gray-700 hidden sm:block">
                <span className="font-medium">
                  {userProfile?.prenom} {userProfile?.nom}
                </span>
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {userProfile?.role === 'admin' ? 'Administrateur' : 
                   userProfile?.role === 'anim' ? 'Animateur' :
                   userProfile?.role === 'coordi' ? 'Coordinateur' : userProfile?.role}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors p-2 sm:p-0"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl z-50 overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="font-semibold text-gray-900">Menu</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-3">
              {sidebarContent}
            </div>
            <div className="p-3 border-t border-gray-200 sm:hidden">
              <div className="text-sm text-gray-700 px-3 py-2">
                <span className="font-medium">
                  {userProfile?.prenom} {userProfile?.nom}
                </span>
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {userProfile?.role === 'admin' ? 'Administrateur' : 
                   userProfile?.role === 'anim' ? 'Animateur' :
                   userProfile?.role === 'coordi' ? 'Coordinateur' : userProfile?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <nav className={`hidden lg:block bg-white shadow-sm min-h-[calc(100vh-4rem)] border-r border-gray-200 transition-all duration-200 flex-shrink-0 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}>
          <div className="p-3">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title={sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'}
              >
                <ChevronLeft className={`h-4 w-4 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {sidebarContent}
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 min-w-0 p-3 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
